import React, { useEffect, useRef } from 'react';
import { Animated, StyleSheet, View } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Feather';
import { AURA_META, type AuraType } from '../data/dayVibesQuestions';

const BADGE_SIZE = 88;
const BADGE_INNER = 80;

const AuraBadge: React.FC<{ auraType: AuraType }> = React.memo(({ auraType }) => {
  const meta = AURA_META[auraType];
  const shimmerAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    let active = true;
    const run = () => {
      if (!active) return;
      shimmerAnim.setValue(0);
      Animated.sequence([
        Animated.timing(shimmerAnim, { toValue: 1, duration: 1100, useNativeDriver: true }),
        Animated.delay(4400),
      ]).start(({ finished }) => {
        if (finished && active) run();
      });
    };
    const timer = setTimeout(run, 900);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [shimmerAnim]);

  const shimmerX = shimmerAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [-BADGE_SIZE, BADGE_SIZE],
  });

  return (
    <View style={styles.shadow}>
      <View style={styles.frame}>
        <LinearGradient
          colors={['#4C4C66', '#18182C', '#38384E', '#0E0E1E']}
          locations={[0, 0.3, 0.68, 1]}
          start={{ x: 0.18, y: 0 }}
          end={{ x: 0.82, y: 1 }}
          style={StyleSheet.absoluteFill}
        />
        <View style={styles.bevel} />
        <View style={[styles.enamel, { borderColor: `${meta.color}1E` }]}>
          <LinearGradient
            colors={['#0D0F1C', '#060710', '#0B0C18']}
            start={{ x: 0.12, y: 0 }}
            end={{ x: 0.88, y: 1 }}
            style={StyleSheet.absoluteFill}
          />
          <View style={[StyleSheet.absoluteFill, styles.auraTint, { backgroundColor: `${meta.color}14` }]} />
          <Icon name={meta.icon} size={26} color="rgba(168,178,198,0.84)" />
          <Animated.View
            style={[styles.shimmer, { transform: [{ translateX: shimmerX }] }]}
            pointerEvents="none"
          >
            <LinearGradient
              colors={['transparent', 'rgba(255,255,255,0.06)', 'rgba(255,255,255,0.11)', 'rgba(255,255,255,0.06)', 'transparent']}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={StyleSheet.absoluteFill}
            />
          </Animated.View>
          <LinearGradient
            colors={['rgba(255,255,255,0.07)', 'transparent']}
            start={{ x: 0.5, y: 0 }}
            end={{ x: 0.5, y: 1 }}
            style={styles.catchLight}
            pointerEvents="none"
          />
        </View>
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  shadow: {
    width: BADGE_SIZE,
    height: BADGE_SIZE,
    borderRadius: BADGE_SIZE / 2,
    transform: [{ rotate: '-7deg' }],
    shadowColor: '#000',
    shadowOffset: { width: 3, height: 5 },
    shadowOpacity: 0.58,
    shadowRadius: 11,
    elevation: 12,
  },
  frame: {
    width: BADGE_SIZE,
    height: BADGE_SIZE,
    borderRadius: BADGE_SIZE / 2,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
  },
  bevel: {
    position: 'absolute',
    top: 4,
    width: 44,
    height: 1,
    borderRadius: 1,
    backgroundColor: 'rgba(255,255,255,0.22)',
    zIndex: 2,
  },
  enamel: {
    width: BADGE_INNER,
    height: BADGE_INNER,
    borderRadius: BADGE_INNER / 2,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 0.5,
  },
  auraTint: {
    borderRadius: BADGE_INNER / 2,
  },
  shimmer: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
  catchLight: {
    position: 'absolute',
    top: 0,
    left: 10,
    right: 10,
    height: 16,
    borderRadius: 8,
  },
});

export default AuraBadge;
